import { supabase } from '@/integrations/supabase/client';

// Storage keys
const STORAGE_KEYS = {
  EMAIL: 'tempvault_email',
  TOKEN: 'tempvault_token',
  INBOX_ID: 'tempvault_inbox_id',
  CREATED_AT: 'tempvault_created_at',
  EXPIRES_AT: 'tempvault_expires_at',
};

export interface InboxSession {
  email: string;
  token: string;
  inboxId: string;
  createdAt: number;
  expiresAt: number;
}

// Get inbox from localStorage if valid
const getStoredInbox = (): InboxSession | null => {
  const email = localStorage.getItem(STORAGE_KEYS.EMAIL);
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  const inboxId = localStorage.getItem(STORAGE_KEYS.INBOX_ID);
  const createdAt = localStorage.getItem(STORAGE_KEYS.CREATED_AT);
  const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);

  if (email && token && inboxId && createdAt && expiresAt) {
    const expires = parseInt(expiresAt, 10);
    if (Date.now() < expires) {
      return {
        email,
        token,
        inboxId,
        createdAt: parseInt(createdAt, 10),
        expiresAt: expires,
      };
    }
  }
  return null;
};

// Store inbox in localStorage
const storeInbox = (session: InboxSession): void => {
  localStorage.setItem(STORAGE_KEYS.EMAIL, session.email);
  localStorage.setItem(STORAGE_KEYS.TOKEN, session.token);
  localStorage.setItem(STORAGE_KEYS.INBOX_ID, session.inboxId);
  localStorage.setItem(STORAGE_KEYS.CREATED_AT, session.createdAt.toString());
  localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, session.expiresAt.toString());
};

// Clear stored inbox
const clearStoredInbox = (): void => {
  localStorage.removeItem(STORAGE_KEYS.EMAIL);
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.INBOX_ID);
  localStorage.removeItem(STORAGE_KEYS.CREATED_AT);
  localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
};

// Create headers with Authorization token
const createAuthHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Create new inbox via backend
export const createInbox = async (): Promise<InboxSession> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=create`,
    {
      method: 'GET',
      headers: createAuthHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to create inbox');
  }

  const result = await response.json();
  
  const session: InboxSession = {
    email: result.email,
    token: result.token,
    inboxId: result.inboxId,
    createdAt: new Date(result.createdAt).getTime(),
    expiresAt: new Date(result.expiresAt).getTime(),
  };

  storeInbox(session);
  return session;
};

// Verify existing inbox is still valid
export const verifyInbox = async (token: string): Promise<InboxSession | null> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=get`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    }
  );

  if (!response.ok) {
    clearStoredInbox();
    return null;
  }

  const result = await response.json();
  
  const session: InboxSession = {
    email: result.email,
    token: result.token,
    inboxId: result.inboxId,
    createdAt: new Date(result.createdAt).getTime(),
    expiresAt: new Date(result.expiresAt).getTime(),
  };

  storeInbox(session);
  return session;
};

// Get or create inbox session
export const getOrCreateInbox = async (): Promise<InboxSession> => {
  // Check localStorage first
  const stored = getStoredInbox();
  
  if (stored) {
    // Verify it's still valid on the backend
    const verified = await verifyInbox(stored.token);
    if (verified) {
      return verified;
    }
  }

  // Create new inbox
  return createInbox();
};

// Generate new inbox (reset)
export const generateNewInbox = async (): Promise<InboxSession> => {
  clearStoredInbox();
  return createInbox();
};

// Fetch emails for inbox
export const fetchEmails = async (token: string): Promise<any[]> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=emails`,
    {
      method: 'GET',
      headers: createAuthHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch emails');
  }

  return response.json();
};

// Mark email as read
export const markEmailAsRead = async (token: string, emailId: string): Promise<void> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=read&emailId=${emailId}`,
    {
      method: 'POST',
      headers: createAuthHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to mark email as read');
  }
};

// Delete email
export const deleteEmailById = async (token: string, emailId: string): Promise<void> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=delete&emailId=${emailId}`,
    {
      method: 'DELETE',
      headers: createAuthHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to delete email');
  }
};

// Send test email to inbox
export const sendTestEmail = async (token: string): Promise<void> => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/inbox?action=test`,
    {
      method: 'POST',
      headers: createAuthHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to send test email');
  }
};

// Calculate time remaining until expiry
export const getTimeRemaining = (expiresAt: number): { hours: number; minutes: number; expired: boolean } => {
  const now = Date.now();
  const remaining = expiresAt - now;

  if (remaining <= 0) {
    return { hours: 0, minutes: 0, expired: true };
  }

  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

  return { hours, minutes, expired: false };
};

// Check if content contains OTP pattern
export const detectOTP = (content: string): string | null => {
  const otpPatterns = [
    /\b(\d{6})\b/g,
    /\b(\d{4})\b/g,
    /\b(\d{8})\b/g,
    /code[:\s]+(\d{4,8})/gi,
    /otp[:\s]+(\d{4,8})/gi,
    /verification[:\s]+(\d{4,8})/gi,
  ];

  for (const pattern of otpPatterns) {
    const match = content.match(pattern);
    if (match) {
      const digits = match[0].replace(/\D/g, '');
      if (digits.length >= 4 && digits.length <= 8) {
        return digits;
      }
    }
  }

  return null;
};
